/* ─── API Client - Handles all backend communication ─── */
export class ApiClient {
  constructor(baseUrl = '') {
    this.baseUrl = baseUrl;
    this.cache = new Map();
    this.cacheTTL = 30000; // 30 seconds default
  }

  async request(path, options = {}) {
    const url = `${this.baseUrl}${path}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(error.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API Error [${config.method || 'GET'} ${path}]:`, error);
      throw error;
    }
  }

  get(path, options = {}) {
    return this.request(path, { ...options, method: 'GET' });
  }

  post(path, body, options = {}) {
    return this.request(path, {
      ...options,
      method: 'POST',
      body: JSON.stringify(body)
    });
  }

  // Cached GET requests
  async getCached(path, ttl = this.cacheTTL) {
    const now = Date.now();
    const cached = this.cache.get(path);

    if (cached && (now - cached.timestamp) < ttl) {
      return cached.data;
    }

    const data = await this.get(path);
    this.cache.set(path, { data, timestamp: now });
    return data;
  }

  invalidateCache(path) {
    this.cache.delete(path);
  }

  clearCache() {
    this.cache.clear();
  }

  // Specific API methods
  async getBriefing() {
    return this.getCached('/api/briefing/v2');
  }

  async getWeather(lat, lon) {
    return this.getCached(`/api/weather?lat=${lat}&lon=${lon}`, 600000); // 10 min cache
  }

  async getNews(category = 'all') {
    return this.getCached(`/api/news?category=${category}`, 300000); // 5 min cache
  }

  async getCalendar() {
    return this.getCached('/api/calendar/today', 60000); // 1 min cache
  }

  async getHistory(filter = 'all') {
    return this.get(`/api/history?filter=${filter}`);
  }

  async getStats() {
    return this.get('/api/stats');
  }

  async sendChat(message, sessionId) {
    return this.post('/api/chat', { message, sessionId });
  }

  async startVoiceSession() {
    return this.post('/api/voice/start', {});
  }

  async stopVoiceSession() {
    return this.post('/api/voice/stop', {});
  }

  async sendVoiceAudio(audioBlob) {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');
    return this.request('/api/voice/audio', {
      method: 'POST',
      body: formData,
      headers: {} // Let browser set content-type with boundary
    });
  }

  async getNotes() {
    return this.get('/api/notes');
  }

  async getToday() {
    return this.getCached('/api/today', 120000); // 2 min cache
  }
}

/* ─── WebSocket Manager for real-time updates ─── */
export class WebSocketManager {
  constructor(url) {
    this.url = url;
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.listeners = new Map();
    this.connected = false;
  }

  connect() {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    try {
      this.ws = new WebSocket(this.url);
      this.ws.onopen = () => this.onOpen();
      this.ws.onmessage = (e) => this.onMessage(e);
      this.ws.onclose = () => this.onClose();
      this.ws.onerror = (e) => this.onError(e);
    } catch (error) {
      console.error('WebSocket connection failed:', error);
      this.scheduleReconnect();
    }
  }

  onOpen() {
    console.log('WebSocket connected');
    this.connected = true;
    this.reconnectAttempts = 0;
    this.emit('open');
  }

  onMessage(event) {
    try {
      const data = JSON.parse(event.data);
      this.emit(data.type, data.payload);
    } catch (error) {
      console.error('WebSocket message parse error:', error);
    }
  }

  onClose() {
    console.log('WebSocket disconnected');
    this.connected = false;
    this.emit('close');
    this.scheduleReconnect();
  }

  onError(error) {
    console.error('WebSocket error:', error);
    this.emit('error', error);
  }

  scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    console.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
    setTimeout(() => this.connect(), delay);
  }

  send(type, payload) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type, payload }));
    }
  }

  on(event, handler) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(handler);
  }

  off(event, handler) {
    if (!this.listeners.has(event)) return;
    const handlers = this.listeners.get(event);
    const index = handlers.indexOf(handler);
    if (index > -1) handlers.splice(index, 1);
  }

  emit(event, payload) {
    if (!this.listeners.has(event)) return;
    this.listeners.get(event).forEach(handler => handler(payload));
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }
}

/* ─── Export ─── */
export const Api = {
  ApiClient,
  WebSocketManager
};